import * as React from 'react';
import { ConfigurationScope, InvitationsSettings, Period, Language, Template, LandingPageLocales, CustomTemplateParams,
    DefaultWebsite, InvitationTrigger, InvitationTriggerMap, TooltipType, GeneralSettings } from '../Model';
import { Text, Select, NotificationAlert, NotificationType, TrustpilotSpinner, Label, LabelSize, Link } from '../CommonUI';
import Button from '../CommonUI/Control/Button';
import '../Assets/styles/component.css';
import '../Assets/styles/general.css';
import { Flex, Box } from '../CommonUI/Layout';
import { consentIsAccepted } from '../Api/businessConsentsClient';
import { saveInvitationSettings, loadTemplates, getInvitationSettings } from '../Api/invitationSettingsClient';
import { integrationConfig, getIntegrationConfig } from '../Api/configapiClient';
import { Text as T } from 'ilenia';
import { Auth } from '@trustpilot/ecommscript-integrationappauth';
import { StyleSheet, css } from 'aphrodite';
import { CustomEmailTemplates } from '../Components';
import { Utils } from '../Utils';

interface InvitationsProps {
    configurationScopeTree?: ConfigurationScope[];
    source: string;
    integrationKey: string;
    onLoggedInChange: (toggle: boolean) => void;
    invitationTrigger: InvitationTrigger;
    onChange: (generalSettings: GeneralSettings) => void;
    onSettingsSave: () => void;
    tierName: string;
    onConsentChange: (toggle: boolean) => void;
}

interface InvitationState {
    invitationSettings?: InvitationsSettings;
    templates?: Template[];
    templateLanguage: Language;
    businessUnitId: string;
    saveError?: string;
    customTemplateParams: CustomTemplateParams[];
    allowedDomains?: Set<string>;
    configurationScopeTree?: ConfigurationScope[];
}

export class Invitations extends React.Component<InvitationsProps, InvitationState> {
    private mounted = true;

    constructor(props: InvitationsProps) {
        super(props);
        this.state = {
            invitationSettings: undefined,
            templateLanguage: Language.English,
            businessUnitId: '',
            customTemplateParams: [],
        };
        this.onSenderNameChange = this.onSenderNameChange.bind(this);
        this.onReplyToEmailChange = this.onReplyToEmailChange.bind(this);
        this.onDelayInDaysChange = this.onDelayInDaysChange.bind(this);
        this.onLandingPageLocaleChange = this.onLandingPageLocaleChange.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onTemplateIdChange = this.onTemplateIdChange.bind(this);
        this.onTemplateLanguageChange = this.onTemplateLanguageChange.bind(this);
        this.getAvailableScopeTree = this.getAvailableScopeTree.bind(this);
        this.onAddCustomSettings = this.onAddCustomSettings.bind(this);
        this.setCustomWebsite = this.setCustomWebsite.bind(this);
        this.setCustomTemplateLanguage = this.setCustomTemplateLanguage.bind(this);
        this.setCustomTemplateId = this.setCustomTemplateId.bind(this);
        this.saveAllSettings = this.saveAllSettings.bind(this);
        this.onInvitationTriggerKeyChange = this.onInvitationTriggerKeyChange.bind(this);
    }

    public componentDidMount() {
        const { integrationKey, source, configurationScopeTree } = this.props;
        if (integrationKey) {
            this.pullInitialData(integrationKey).then(({ isLoggedIn, consentResponse, config }) => {
                if (isLoggedIn && config) {
                    if (consentResponse && consentResponse.consentIsAccepted) {
                        analytics.page();
                        this.getInvitationsTabSettings(consentResponse.businessUnitId).then((settings) => {
                            const [invitationSettings, templates] = settings;
                            if (invitationSettings && templates) {
                                const templateLanguage = this.getTemplateLanguage(templates, invitationSettings);
                                if (this.mounted) {
                                    const allowedDomains = this.getAllowedDomains(config.allowedDomains || []);
                                    this.setState({
                                        invitationSettings: { ...invitationSettings },
                                        templates,
                                        templateLanguage,
                                        businessUnitId: consentResponse.businessUnitId,
                                        customTemplateParams: config.templateMap || [],
                                        allowedDomains,
                                        configurationScopeTree: this.getConfigurationTree(source, configurationScopeTree, Array.from(allowedDomains)),
                                    });
                                }
                            } else {
                                NotificationAlert.alert(NotificationType.Error, 'Notifications.loadDataError');
                            }
                        });
                    } else {
                        this.props.onConsentChange(false);
                    }
                } else {
                    this.props.onLoggedInChange(false);
                }
            });
        }
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    public async pullInitialData(integrationKey: string) {
        const isLoggedIn = await Auth.isLoggedIn(process.env.REACT_APP_AuthenticationApiUrl!, process.env.REACT_APP_CookiesDomain!);
        const [consentResponse, config] = await Promise.all([
            consentIsAccepted(integrationKey),
            getIntegrationConfig(integrationKey),
        ]);
        return { isLoggedIn, consentResponse, config };
    }

    public getInvitationsTabSettings(businessUnitId: string) {
        return Promise.all([
            getInvitationSettings(businessUnitId),
            loadTemplates(businessUnitId)
        ]);
    }

    public getTemplateLanguage(templates: Template[], settings: InvitationsSettings) {
        const template = templates.find((tpl) => tpl.id === settings.templateId);
        return template ? template.locale : Language.English;
    }

    public templateOptionsForLanguage(language: string) {
        const templates = this.state.templates!;
        const currentLocaleTemplates = language ?
            templates.filter((tpl) => tpl.locale === language || !tpl.isDefaultTemplate) :
            templates.filter((tpl) => !tpl.isDefaultTemplate);

        return currentLocaleTemplates
            .filter((tpl) => !tpl.name.endsWith('(Deprecated)'))
            .sort((prev, next) => {
                return (prev.isDefaultTemplate === next.isDefaultTemplate) ? 0 : prev.isDefaultTemplate ? -1 : 1;
            });
    }

    public onSenderNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        const senderName = event.target.value;
        const invitationSettings = { ...this.state.invitationSettings!, senderName };
        this.setState({ invitationSettings });
    }

    public onReplyToEmailChange(type: string, value: string) {
        const replyToEmail = value;
        const invitationSettings = { ...this.state.invitationSettings!, replyToEmail };
        this.setState({ invitationSettings });
    }

    public onDelayInDaysChange(type: string, value: string) {
        const delayInDays = value as Period;
        // Todo: remove productReviewInvitationDelayInDays and useSeparateProductReviewInvitations and use
        //  onProductReviewInvitationDelayInDaysChange() and onUseSeparateProductReviewInvitationsChange() instead
        //  after product reviews delay UI is added.
        const productReviewInvitationDelayInDays = value as Period;
        const useSeparateProductReviewInvitations = false;
        const invitationSettings = { ...this.state.invitationSettings!, delayInDays, productReviewInvitationDelayInDays,
                                     useSeparateProductReviewInvitations };
        this.setState({ invitationSettings });
    }

    public onProductReviewInvitationDelayInDaysChange(type: string, value: string) {
        const productReviewInvitationDelayInDays = value as Period;
        const invitationSettings = {...this.state.invitationSettings!, productReviewInvitationDelayInDays };
        this.setState({ invitationSettings });
    }

    public onUseSeparateProductReviewInvitationsChange(value: boolean) {
        const invitationSettings = { ...this.state.invitationSettings!, useSeparateProductReviewInvitations: value };
        this.setState({ invitationSettings });
    }

    public onLandingPageLocaleChange(type: string, value: string) {
        const landingPageLocale = value as Language;
        const invitationSettings = { ...this.state.invitationSettings!, landingPageLocale };
        this.setState({ invitationSettings });
    }

    public onTemplateLanguageChange(type: string, value: string) {
        const templateLanguage = value as Language;
        this.setState({ templateLanguage });
        this.updateTemplateIdIfNeeded(templateLanguage);
    }

    public updateTemplateIdIfNeeded(newTemplateLanguage: string) {
        const { invitationSettings } = this.state;
        const templateOptions = this.templateOptionsForLanguage(newTemplateLanguage);
        const template = templateOptions.find((tpl) => tpl.id === invitationSettings!.templateId);
        if (!template) {
            this.onTemplateIdChange('', templateOptions[0].id);
        }
    }

    public onTemplateIdChange(type: string, value: string) {
        const templateId = value;
        const invitationSettings = { ...this.state.invitationSettings!, templateId };
        this.setState({ invitationSettings });
    }

    public saveAllSettings() {
        const { onSettingsSave } = this.props;
        const {
            invitationSettings,
            businessUnitId,
            customTemplateParams,
            configurationScopeTree,
        } = this.state;
        onSettingsSave();
        return Promise.all([
            saveInvitationSettings(businessUnitId, invitationSettings!),
            integrationConfig(businessUnitId, { configurationScopeTree, templateMap: customTemplateParams }),
        ]);
    }

    public onSave() {
        this.saveAllSettings().then((response) => {
            const [saved] = response;
            if (this.mounted) {
                if (saved) {
                    this.setState({saveError: 'Notifications.saveMessage'});
                } else {
                    this.setState({saveError: 'invitations.error'});
                }
            }
        });
    }

    public onChange(next: (event: any) => void) {
        return (event: any) => {
            return next(event);
        };
    }

    public onSelectChange(next: (type: any, value: any) => void) {
        return (type: any, value: any) => {
            return next(type, value);
        };
    }

    public setCustomWebsite(customTemplateParams: CustomTemplateParams[]) {
        this.setState({ customTemplateParams });
    }

    public setCustomTemplateLanguage(customTemplateParams: CustomTemplateParams[]) {
        this.setState({ customTemplateParams });
    }

    public setCustomTemplateId(customTemplateParams: CustomTemplateParams[]) {
        this.setState({ customTemplateParams });
    }

    public getAvailableScopeTree() {
        const { customTemplateParams, allowedDomains, configurationScopeTree } = this.state;

        if (configurationScopeTree && configurationScopeTree.length > 0) {
            return configurationScopeTree.filter((scope) =>
                !customTemplateParams.some((selected) =>
                    Utils.arraysEqual(scope.ids, selected.scopeTree.ids)) &&
                allowedDomains &&
                allowedDomains.has(scope.domain)
            );
        } else {
            return [];
        }
    }

    public onAddCustomSettings() {
        const { invitationSettings, templateLanguage, customTemplateParams } = this.state;
        const { templateId } = invitationSettings!;
        const notSelected = this.getAvailableScopeTree();

        if (notSelected.length > 0) {
            const newCustomTemplateParam = {
                templateId,
                languageId: templateLanguage,
                scopeTree: notSelected[0],
            };

            this.setState({ customTemplateParams: [...customTemplateParams, newCustomTemplateParam] });
        }
    }

    public onInvitationTriggerKeyChange(type: string, value: string) {
        const invitationTriggerData = {
            [type]: value,
        };
        this.props.onChange(invitationTriggerData);
    }

    public tooltipBySource(source: string) {
        return InvitationTriggerMap[source.toLowerCase()].tooltip;
    }

    public getConfigurationTree(source: string, tree: ConfigurationScope[] = [], allowedDomains: string[] = []) {
        if (source === 'Shopify' && allowedDomains) {
            const newTree = [
                ...new Set([
                    ...tree.map((leaf) => leaf.domain),
                    ...allowedDomains
                        .filter((domain) =>  domain.endsWith('myshopify.com'))
                        .map((domain) => domain.replace(/^www./, '')),
                ])
            ].map((domain) => ({
                    ids: [domain],
                    names: { site: '', store: domain, view: '' },
                    domain,
                    additional: true,
                }));
            return newTree as ConfigurationScope[];
        } else {
            return tree;
        }
    }

    public render() {
        const { source, invitationTrigger, tierName } = this.props;
        const { templates, invitationSettings, templateLanguage, customTemplateParams, allowedDomains, configurationScopeTree } = this.state;
        if (!templates || !invitationSettings) {
            return <TrustpilotSpinner active={true} height="500px"/>;
        }
        const { senderName, replyToEmail, delayInDays, landingPageLocale, templateId,
                verifiedReplyToEmailAddresses } = invitationSettings;
        const templateOptions = this.templateOptionsForLanguage(templateLanguage)
            .map((template) => ({ value: template.id, label: template.name }));
        const templateObject = templates.find((template) => template.id === templateId);
        const templateValue = (templateObject)
            ? { value: templateObject.id, label: templateObject.name }
            : { value: '', label: '' };
        const showTemplateParams = this.getAvailableScopeTree().length > 0 || customTemplateParams.length > 0;
        const showAddNewTemplateSelector = this.getAvailableScopeTree().length > 0;

        const allWebsitesParams = configurationScopeTree && configurationScopeTree.length === 1 ? {
            value: configurationScopeTree[0].domain,
            data: [configurationScopeTree[0].domain],
        } : {
            value: 'default',
            data: Object.keys(DefaultWebsite).map((type) => DefaultWebsite[type]),
        };

        return (
            <Box pb={30} mt={0} style={{position: 'relative'}}>
                <SupportBox source={source} tierName={tierName} />
                <Flex>
                    <Box pl={20}>
                        <Label
                            label="invitations.section.timing.title"
                        />
                    </Box>
                    <Select
                        id="invitationTrigger"
                        labelSize={LabelSize.Small}
                        label="invitations.section.timing.triggerActionLabel"
                        value={invitationTrigger}
                        localize={true}
                        onChangeEvent={this.onInvitationTriggerKeyChange}
                        data={Object.keys(InvitationTrigger).map((type) => InvitationTrigger[type])}
                        tooltip={{
                            text: this.tooltipBySource(source),
                            type: TooltipType.html,
                            interpolations: {p1: '<p key=1>', p2: '<p key=2>', p: '</p>', b1: '<b key=1>', b2: '<b key=2>', b: '</b>'}
                        }}
                    />
                    <Select
                        id="delay-in-days-select"
                        labelSize={LabelSize.Small}
                        label="invitations.section.timing.delayLabel"
                        localize={true}
                        value={delayInDays.toString()}
                        data={Object.keys(Period).map((type) => Period[type])}
                        onChangeEvent={this.onSelectChange(this.onDelayInDaysChange)}
                    />
                    <Divider />
                    <Box pl={20}>
                        <Label
                            label="invitations.section.email.title"
                        />
                    </Box>
                    <Text
                        label="invitations.section.email.senderNameLabel"
                        labelSize={LabelSize.Small}
                        value={senderName}
                        onChangeEvent={this.onChange(this.onSenderNameChange)}
                    />
                    <Select
                        id="reply-to-email-select"
                        labelSize={LabelSize.Small}
                        label="invitations.section.email.replyAddressLabel"
                        value={replyToEmail}
                        data={verifiedReplyToEmailAddresses}
                        onChangeEvent={this.onSelectChange(this.onReplyToEmailChange)}
                    />
                    <Select
                        id="website-select"
                        labelSize={LabelSize.Small}
                        label="invitations.section.email.landingPageLabel"
                        value={landingPageLocale}
                        data={Object.keys(LandingPageLocales).map((type) => LandingPageLocales[type])}
                        localize={true}
                        onChangeEvent={this.onSelectChange(this.onLandingPageLocaleChange)}
                    />
                    <Divider />
                    <Box pl={20}>
                        <Label
                            label="invitations.section.template.title"
                        />
                    </Box>
                    {!showTemplateParams &&
                        <React.Fragment>
                            <Select
                                id="language-select"
                                labelSize={LabelSize.Small}
                                label="invitations.section.template.templateCategoryLabel"
                                localize={true}
                                value={templateLanguage}
                                data={Object.keys(Language).map((type) => Language[type])}
                                onChangeEvent={this.onSelectChange(this.onTemplateLanguageChange)}
                            />
                            <Select
                                id="email-template-select"
                                labelSize={LabelSize.Small}
                                label="invitations.section.template.templateLabelSingle"
                                value={templateValue}
                                data={templateOptions}
                                onChangeEvent={this.onSelectChange(this.onTemplateIdChange)}
                            />
                        </React.Fragment>
                    }
                    {showTemplateParams &&
                        <React.Fragment>
                            <Flex
                                flexDirection={'row'}
                                alignItems={'flex-end'}
                                justifyContent={'flex-start'}
                                style={{width: '100%'}}
                            >
                                <Select
                                    id="language-select"
                                    mt="0"
                                    label="invitations.section.template.templateCategoryLabel"
                                    labelSize={LabelSize.Small}
                                    localize={true}
                                    value={templateLanguage}
                                    data={Object.keys(Language).map((type) => Language[type])}
                                    onChangeEvent={this.onSelectChange(this.onTemplateLanguageChange)}
                                />
                                <Select
                                    id="email-template-select"
                                    mt="0"
                                    label="invitations.section.template.templateLabel"
                                    labelSize={LabelSize.Small}
                                    value={templateValue}
                                    data={templateOptions}
                                    onChangeEvent={this.onSelectChange(this.onTemplateIdChange)}
                                />
                                <Select
                                    id="all-websites"
                                    mt="0"
                                    label="invitations.section.template.useTemplateLabel"
                                    labelSize={LabelSize.Small}
                                    localize={true}
                                    disabled={true}
                                    {...allWebsitesParams}
                                />
                                <div style={{width: '65px'}}/>
                            </Flex>
                            <CustomEmailTemplates
                                configurationScopeTree={configurationScopeTree}
                                customTemplateParams={customTemplateParams}
                                templates={templates}
                                allowedDomains={allowedDomains}
                                setCustomWebsite={this.setCustomWebsite}
                                setCustomTemplateLanguage={this.setCustomTemplateLanguage}
                                setCustomTemplateId={this.setCustomTemplateId}
                            />
                            {showAddNewTemplateSelector &&
                                <Box pr={20} mt={32} width="auto">
                                    <span className={css(styles.span, styles.spanPseudo)}>
                                        <Link
                                            label="invitations.emailTemplateAddSettings"
                                            onClick={this.onAddCustomSettings}
                                            track={{
                                                placement: 'Under Website selector',
                                                target: 'Integration app invitation template',
                                                name: 'add-invitation-template',
                                                source,
                                                tierName,
                                            }}
                                        />
                                    </span>
                                </Box>
                            }
                        </React.Fragment>
                    }
                    <Box mt={16}>
                        <Button
                            onClick={this.onSave}
                            saveTextId={this.state.saveError}
                            track={{
                                placement: 'Bottom left corner',
                                action: 'Settings saved in invitations tab',
                                name: 'invitations-save-settings-button',
                                source,
                                tierName,
                            }}
                        >
                            <T id="invitations.save" />
                        </Button>
                    </Box>
                </Flex>
            </Box>
        );
    }

    private getAllowedDomains(allowedDomains: string[]) {
        const whitelistedDomains = new Set<string>();
        if (process && process.env && process.env.REACT_APP_WhitelistedDomains && process.env.REACT_APP_WhitelistedDomains.length > 0) {
            process.env.REACT_APP_WhitelistedDomains.split(',').forEach((domain) => {
                if (domain && typeof domain === 'string') {
                    whitelistedDomains.add(domain);
                }
            });
        }
        if (allowedDomains && allowedDomains.length > 0) {
            allowedDomains.forEach((domain) => {
                if (domain && typeof domain === 'string') {
                    whitelistedDomains.add(domain);
                }
            });
        }
        return whitelistedDomains;
    }
}

function Divider() {
    return (
        <Flex style={{width: '100%', padding: '26px 18px 14px 18px'}}>
            <Box className={css(styles.divider)} />
         </Flex>
    );
}

function SupportBox(props: { source: string; tierName: string; }) {
    const {source, tierName} = props;
    const troubleshootArticleUrl = process.env.REACT_APP_SupportArticleTroubleshooting;
    const supportArticleUrl = Utils.getSupportArticleUrl(source);

    return (
        <Box className={css(styles.supportBoxContainer)}>
            {supportArticleUrl &&
                <Link
                    label="invitations.support.article"
                    pl={0}
                    href={supportArticleUrl}
                    track={{
                        placement: 'Top right corner',
                        target: 'Integrations support article',
                        name: 'navigate-to-support-article',
                        source,
                        tierName,
                    }}
                />
            }
            <Link
                label="invitations.support.guide"
                pl={0}
                href={troubleshootArticleUrl}
                track={{
                    placement: 'Top right corner',
                    target: 'Integrations troubleshooting guide',
                    name: 'navigate-to-troubleshooting-guide',
                    source,
                    tierName,
                }}
            />
        </Box>
    );
}

const styles = StyleSheet.create({
    supportBoxContainer: {
        position: 'absolute',
        top: 0,
        right: 0,
        background: '#F9F9FA',
        width: '251px',
        marginTop: '24px',
        padding: '0px 10px 10px 10px'
    },
    span: {
        fontFamily: 'Lato',
        fontSize: '16px',
        fontWeight: 'normal',
        fontStyle: 'normal',
        fontStretch: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: '#4a90e2',
        textDecoration: 'none'
    },
    spanPseudo: {
        ':hover': {
            color: '#2471b3',
            cursor: 'pointer'
        },
        ':focus': {
            borderRadius: '2px',
            border: 'solid 2px #54a5e5',
            outlineStyle: 'none'
        }
    },
    divider: {
        borderBottom: '1px solid #DCDCE6',
    }
});



// WEBPACK FOOTER //
// ./src/Containers/Invitations.tsx